// Per-questionnaire vendor status for the picker. Two independent signals:
//  - "admin returned" questions — rejected (sent back) or the admin left the
//    last comment in the thread. These drive the red "needs your action" count
//    (isAdminReturned / adminReturnedActionCount, also used for category tab
//    badges). `pending` (answered, awaiting admin review) and approved / N-A are
//    NOT admin-returned.
//  - whether the vendor has finished the questionnaire — all visible required
//    questions complete — passed in by the caller as `fullyAnswered`.
// State precedence: admin-returned > not-finished > answered-but-unsent >
// under-review > compliant. So a questionnaire that was sent but isn't finished
// reads "awaiting your response" rather than a cleared status. (Admin-side
// badges live in outstandingActions.ts.)
import { reviewTurn } from './reviewState.ts';
import type { Question, Submission } from './types.ts';

export type QuestionnaireVendorStatus = {
  actionCount: number;
  state:
    | 'action_needed'
    | 'awaiting_response'
    | 'pending_submission'
    | 'under_review'
    | 'compliant';
};

type BadgeSubmission = Pick<Submission, 'status' | 'latest_comment_author' | 'question_id'>;

/** True when the admin has returned this submission to the vendor for action. */
export function isAdminReturned(sub: BadgeSubmission | undefined): boolean {
  if (!sub) return false;
  if (sub.status === 'approved' || sub.status === 'not_applicable') return false;
  return sub.status === 'rejected' || sub.latest_comment_author === 'admin';
}

/** Count of questions (within the given set) the admin has returned to the vendor. */
export function adminReturnedActionCount(
  questions: Question[],
  subByQ: Map<string, BadgeSubmission>,
): number {
  let n = 0;
  for (const question of questions) if (isAdminReturned(subByQ.get(question.id))) n += 1;
  return n;
}

/**
 * Derive the vendor-action status for a single questionnaire. Pass the questions
 * already scoped to that questionnaire, the vendor's submission list, and two
 * gates the caller computes: `fullyAnswered` (computeProgress(...).progress ===
 * 100) and `submitted` (questionnaireSubmitted — keyed on submitted_at).
 *
 * Precedence, on the vendor's already-published data (unpublished admin drafts
 * are stripped server-side, so a verdict is only visible post-Notify):
 *  1. any admin-returned item  -> action_needed  ("Action required")
 *  2. not fully answered       -> awaiting_response
 *  3. answered but not submitted-> pending_submission
 *  4. submitted, no clearing verdict published yet -> under_review
 *  5. submitted, all items approved / N-A          -> compliant
 * reviewTurn splits 4 from 5; because the vendor never sees a draft verdict,
 * `compliant` is unreachable until the admin publishes (clicks Notify).
 */
export function questionnaireVendorStatus(
  questions: Question[],
  submissions: BadgeSubmission[],
  opts: { fullyAnswered: boolean; submitted: boolean },
): QuestionnaireVendorStatus {
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));
  const actionCount = adminReturnedActionCount(questions, subByQ);
  if (actionCount > 0) return { actionCount, state: 'action_needed' };
  if (!opts.fullyAnswered) return { actionCount: 0, state: 'awaiting_response' };
  if (!opts.submitted) return { actionCount: 0, state: 'pending_submission' };
  const scoped = questions
    .map((question) => subByQ.get(question.id))
    .filter((s): s is BadgeSubmission => s !== undefined);
  const state = reviewTurn(scoped) === 'compliant' ? 'compliant' : 'under_review';
  return { actionCount: 0, state };
}
